// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

/**
 * What entering a contract amount says about a buyout row's status.
 *
 * Estimate Accuracy only counts a row that is awarded or executed AND carries
 * both a budget and a contract amount. A row created from the estimate starts
 * pending with no contract, so backfilling a finished job meant two
 * interactions per line: type the number, then change the status to say the
 * thing the number already said.
 *
 * Nobody records a contract amount against work they have not bought out, so
 * the second step was ceremony. Typing one promotes the row.
 *
 * Promotes to "awarded" rather than "executed" on purpose: a contract amount
 * is known at award, while execution means signed, and inferring a signature
 * from a number would be claiming something the user did not say. Both count
 * toward accuracy, so nothing is lost by taking the weaker of the two.
 */

export type BuyoutStatus = "pending" | "bidding" | "awarded" | "executed";

/** Statuses that mean a scope has been bought out. Mirrors lib/insights.ts. */
export const AWARDED_STATUSES: BuyoutStatus[] = ["awarded", "executed"];

export function isAwarded(status: string): boolean {
  return (AWARDED_STATUSES as string[]).includes(status);
}

/**
 * The status a row should carry once a contract amount is entered.
 *
 * Only ever promotes. Clearing an amount back to zero leaves the status alone:
 * demoting would quietly undo something the user set deliberately, and a row
 * mid-edit briefly reads as zero.
 */
export function statusAfterContract(current: string, amount: number): BuyoutStatus {
  const status = (current || "pending") as BuyoutStatus;
  if (!(amount > 0)) return status;
  if (status === "pending" || status === "bidding") return "awarded";
  return status;
}

export interface BuyoutRowLike {
  budgetAmount?: number;
  contractAmount?: number;
  status?: string;
}

/**
 * Rows that will actually appear in Estimate Accuracy, and rows that are one
 * number away from doing so.
 *
 * The second figure is the useful one when backfilling: it turns "fill this in
 * some time" into "eleven numbers and this job is in your accuracy history".
 */
export function accuracyReadiness(rows: BuyoutRowLike[]): { counted: number; awaiting: number } {
  let counted = 0;
  let awaiting = 0;
  for (const r of rows || []) {
    const budget = Number(r.budgetAmount) || 0;
    const contract = Number(r.contractAmount) || 0;
    if (budget <= 0) continue;              // nothing to measure against
    if (contract > 0 && isAwarded(r.status || "")) counted++;
    else if (contract <= 0) awaiting++;
  }
  return { counted, awaiting };
}
